import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { ArrowLeft, Loader2, Pencil, FilePenLine } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AccountList, CompanyPicker, PayStateBadge, PaymentDialog, PAY_STATUS_LABEL, SELECT_CLS, useCompanies, accLabel } from "@/components/SalesShared";
import { toast } from "sonner";
import { PayPartsFields, payPartsBody, payPartsError } from "@/components/TxPayParts";
import { RupiahInput } from "@/components/RupiahInput";
import { TransferProofPanel, hasTransfer } from "@/components/TransferProof";
import { useAuth } from "@/context/AuthContext";

const initialParts = (tx) => {
  const init = (tx.payments || []).filter((p) => p.is_initial);
  const sum = (m) => init.filter((p) => p.method === m).reduce((t, p) => t + p.amount, 0);
  const trp = init.find((p) => p.method === "transfer");
  return { cash_part: sum("cash") ? String(sum("cash")) : "", transfer_part: sum("transfer") ? String(sum("transfer")) : "", trp };
};

function EditTxDialog({ tx, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const split = tx.pay_status === "split";
  useEffect(() => {
    if (!open) return;
    const { trp, ...parts } = initialParts(tx);
    setF({ date: tx.date, nota_number: tx.nota_number, customer_name: tx.customer_name, customer_address: tx.customer_address || "", customer_phone: tx.customer_phone || "", amount: String(tx.amount || ""), pay_status: tx.pay_status, ...parts });
  }, [open, tx]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const amt = Number(f.amount) || 0;
  const amtChanged = Math.abs(amt - (tx.amount || 0)) > 0.001;
  const save = async (e) => {
    e.preventDefault();
    if (split && amtChanged) { const err = payPartsError(f, amt, true); if (err) { toast.error(err); return; } }
    setBusy(true);
    try {
      await api.put(`/transactions/${tx.id}`, { date: f.date, nota_number: f.nota_number, customer_name: f.customer_name, customer_address: f.customer_address, customer_phone: f.customer_phone, amount: amt, ...(split && amtChanged ? payPartsBody(f) : {}) });
      toast.success("Transaksi diperbarui."); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-brand/40 text-brand" data-testid="tx-edit-btn"><FilePenLine className="h-3.5 w-3.5 mr-1.5" /> Edit Transaksi</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="tx-edit-dialog">
        <DialogHeader><DialogTitle>Edit Transaksi</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Untuk memperbaiki salah ketik. Cara bayar diubah lewat "Ubah Status Bayar".</p>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tanggal</Label><Input type="date" value={f.date || ""} onChange={set("date")} required className="mt-1.5" data-testid="tx-edit-date" /></div>
            <div><Label>No. Nota</Label><Input value={f.nota_number || ""} onChange={set("nota_number")} required className="mt-1.5" data-testid="tx-edit-nota" /></div>
          </div>
          <div><Label>Nama Customer</Label><Input value={f.customer_name || ""} onChange={set("customer_name")} required className="mt-1.5" data-testid="tx-edit-customer" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Alamat</Label><Input value={f.customer_address || ""} onChange={set("customer_address")} className="mt-1.5" data-testid="tx-edit-address" /></div>
            <div><Label>No. HP</Label><Input value={f.customer_phone || ""} onChange={set("customer_phone")} className="mt-1.5" data-testid="tx-edit-phone" /></div>
          </div>
          <div><Label>Jumlah</Label><RupiahInput value={f.amount || ""} onChange={set("amount")} required className="mt-1.5 font-mono" data-testid="tx-edit-amount" /></div>
          {tx.pay_status === "kredit" && f.date && f.date !== tx.date && <p className="text-xs text-muted-foreground" data-testid="tx-edit-due-note">Jatuh tempo ikut bergeser sesuai perubahan tanggal.</p>}
          {split && amtChanged && <PayPartsFields f={f} setF={setF} companies={[]} amount={amt} testId="tx-edit" hideCompany />}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="tx-edit-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Perubahan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChangeStatusDialog({ tx, companies, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const { trp, ...parts } = initialParts(tx);
    setF({ pay_status: tx.pay_status, company_id: tx.company_id || trp?.company_id || "", account_number: tx.target_account?.account_number || trp?.target_account?.account_number || "", due_date: tx.due_date || "", ...parts });
  }, [open, tx]);
  const setAcc = useCallback((v) => setF((p) => ({ ...p, account_number: v })), []);
  const save = async (e) => {
    e.preventDefault();
    const perr = payPartsError(f, tx.amount || 0);
    if (perr) { toast.error(perr); return; }
    setBusy(true);
    try {
      await api.put(`/transactions/${tx.id}/status`, { ...f, ...payPartsBody(f), company_id: f.company_id || null, due_date: f.due_date || null });
      toast.success("Status bayar diperbarui."); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-brand/40 text-brand" data-testid="tx-change-status-btn"><Pencil className="h-3.5 w-3.5 mr-1.5" /> Ubah Status Bayar</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="tx-status-dialog">
        <DialogHeader><DialogTitle>Ubah Status Bayar</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label>Status Bayar</Label>
            <select value={f.pay_status || ""} onChange={(e) => setF((p) => ({ ...p, pay_status: e.target.value }))} className={`${SELECT_CLS} mt-1.5`} data-testid="tx-status-select">
              {Object.entries(PAY_STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          {f.pay_status === "transfer" && <CompanyPicker companies={companies} value={f.company_id} onChange={(v) => setF((p) => ({ ...p, company_id: v }))} account={f.account_number} onAccountChange={setAcc} testId="tx-status-company" />}
          {f.pay_status === "kredit" && (
            <div><Label>Tanggal Jatuh Tempo</Label><Input type="date" value={f.due_date || ""} onChange={(e) => setF((p) => ({ ...p, due_date: e.target.value }))} required className="mt-1.5" data-testid="tx-status-due" /></div>
          )}
          <PayPartsFields f={f} setF={setF} companies={companies} amount={tx.amount || 0} testId="tx-status" />
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="tx-status-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ label, children, testId }) => (
  <div><p className="text-xs text-muted-foreground">{label}</p><p className="font-medium" data-testid={testId}>{children || "-"}</p></div>
);

export default function TransactionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const readOnly = user?.role !== "pemohon";
  const companies = useCompanies();
  const [tx, setTx] = useState(null);
  const load = useCallback(() => { api.get(readOnly ? `/transfer-proofs/${id}` : `/transactions/${id}`).then((r) => setTx(r.data)).catch(() => setTx(false)); }, [id, readOnly]);
  useEffect(() => { load(); }, [load]);
  if (tx === null) return <div className="p-6"><div className="h-4 rounded-md skeleton-shimmer" /></div>;
  if (tx === false) return <p className="text-muted-foreground">Transaksi tidak ditemukan.</p>;
  const kredit = tx.pay_status === "kredit";
  const withPayments = kredit || tx.pay_status === "split";

  return (
    <div className="space-y-6" data-testid="tx-detail">
      <Link to={readOnly ? "/transfer-proofs" : "/transaction-list"} className="text-sm text-brand inline-flex items-center gap-1 hover:underline"><ArrowLeft className="h-4 w-4" /> Kembali</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Nota {tx.nota_number}</h2>
          <p className="text-sm text-muted-foreground">{tx.customer_name} · {formatDate(tx.date)}{readOnly && tx.owner_name ? ` · Pemohon: ${tx.owner_name}${tx.branch_name ? ` (${tx.branch_name})` : ""}` : ""}</p>
        </div>
        {!readOnly && <div className="flex flex-wrap gap-2">
          <EditTxDialog tx={tx} onDone={load} />
          <ChangeStatusDialog tx={tx} companies={companies} onDone={load} />
          {kredit && <PaymentDialog tx={tx} companies={companies} onDone={load} testId="tx-record-payment-btn" />}
        </div>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2 grid grid-cols-2 gap-4">
          <Row label="Nama Pelanggan">{tx.customer_name}</Row>
          <Row label="No. HP">{tx.customer_phone}</Row>
          <Row label="Alamat Pelanggan">{tx.customer_address}</Row>
          <Row label="Status Bayar" testId="tx-detail-status">{PAY_STATUS_LABEL[tx.pay_status]}</Row>
          {kredit && <Row label="Tanggal Jatuh Tempo" testId="tx-detail-due">{formatDate(tx.due_date)}</Row>}
          {tx.pay_status === "transfer" && <div className="col-span-2"><Row label="PT Tujuan" testId="tx-detail-company">{tx.company_name}</Row><AccountList accounts={tx.target_account ? [tx.target_account] : tx.company_accounts} testId="tx-detail-accounts" /></div>}
        </Card>
        <Card className="p-5 space-y-2">
          <div className="flex justify-between text-sm"><span>Jumlah Tagihan</span><span className="font-mono font-semibold" data-testid="tx-detail-amount">{formatRupiah(tx.amount)}</span></div>
          <div className="flex justify-between text-sm"><span>Sudah Dibayar</span><span className="font-mono text-emerald-700" data-testid="tx-detail-paid">{formatRupiah(tx.paid_total)}</span></div>
          <div className="flex justify-between text-sm border-t pt-2"><span className="font-semibold">Sisa Tagihan</span><span className="font-mono font-bold text-rose-600" data-testid="tx-detail-remaining">{formatRupiah(tx.remaining)}</span></div>
          <div className="pt-1"><PayStateBadge state={tx.payment_state} testId="tx-detail-state" /></div>
        </Card>
      </div>
      {withPayments && (
        <Card className="p-5">
          <h3 className="font-semibold mb-3" style={{ fontFamily: "Outfit" }}>{kredit ? "Riwayat Pembayaran" : "Rincian Pembayaran"} ({tx.payments.length})</h3>
          {tx.payments.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="tx-payments-empty">Belum ada pembayaran.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tx-payments-table">
                <thead className="bg-secondary text-xs uppercase text-muted-foreground"><tr><th className="text-left px-3 py-2">Tanggal</th><th className="text-left px-3 py-2">Metode</th><th className="text-right px-3 py-2">Jumlah</th></tr></thead>
                <tbody>
                  {tx.payments.map((p) => (
                    <tr key={p.id} className="border-t" data-testid={`tx-payment-${p.id}`}>
                      <td className="px-3 py-2">{formatDate(p.date)}{p.is_initial && kredit && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800" data-testid={`tx-payment-dp-${p.id}`}>DP</span>}</td>
                      <td className="px-3 py-2">{p.method === "transfer" ? `Transfer · ${p.company_name}${p.target_account ? ` · ${accLabel(p.target_account)}` : ""}` : `Cash${p.company_name ? ` · ${p.company_name}` : ""}`}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatRupiah(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
      {hasTransfer(tx) && <TransferProofPanel tx={tx} readOnly={readOnly} onDone={load} />}
    </div>
  );
}
